import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useState } from 'react'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { AdminPayload } from '../../../common/models/general-settings'
import FormGroup from '../../../core/components/FormGroup'
import { createGeneralSettingsAdmin } from '../../../core/services/general-settings'
import { MessageBox } from '../messages/MessageBox'

export interface CreateAdminModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
}

interface IAdmins {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  active: boolean
  superAdmin?: boolean | null
  userGroupId?: string | null
}

export const CreateAdminModal = (props: CreateAdminModalProps): JSX.Element => {
  const { showCreateModal, setShowCreateModal } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string().required(),
    password: yup.string().required(),
    active: yup.boolean().required(),
    superAdmin: yup.boolean().nullable().optional(),
    userGroupId: yup.string().nullable().optional(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
  } = useForm<IAdmins>({
    mode: 'onChange',
    resolver: yupResolver<IAdmins>(schema),
  })

  const handleClose = () => {
    setShowCreateModal(false)
  }

  const submit = (values: IAdmins) => {
    // prettier-ignore
    (async () => {
      try {
        const data: AdminPayload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          password: values.password,
          active: values.active,
          superAdmin: true,
        }
        const response = await createGeneralSettingsAdmin(data)
        if (response.status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Pending')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Admins" subtitle="Add New Admin" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              disabled={Object.keys(dirtyFields).length === 0}
              form="create-admin-form"
              type="submit"
              variant="primary"
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="create-admin-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="First Name" error={errors?.firstName?.message}>
            <Input
              isInvalid={!!errors?.firstName?.message}
              {...register('firstName')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>
          <FormGroup label="Last Name" error={errors?.lastName?.message}>
            <Input
              isInvalid={!!errors?.lastName?.message}
              {...register('lastName')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>
          <FormGroup label="Phone Number" error={errors?.phone?.message}>
            <Input
              isInvalid={!!errors?.phone?.message}
              {...register('phone')}
              type="text"
              placeholder="Enter Phone Number"
            />
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <Input
              isInvalid={!!errors?.email?.message}
              {...register('email')}
              type="email"
              placeholder="Enter Email"
            />
          </FormGroup>
          <FormGroup label="Password" error={errors?.password?.message}>
            <Input
              isInvalid={!!errors?.password?.message}
              {...register('password')}
              type="password"
              placeholder="Enter Password"
            />
          </FormGroup>
          <FormGroup
            label="Active"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
